/**
 * Channel Reference Notes — operator-supplied informational payload (SCRUM-382).
 *
 * A LIGHTWEIGHT, OPERATOR-SUPPLIED capture of a manually-recorded channel
 * booking's channel-reported figures — the channel's gross guest payment, OTA
 * commission, and channel-remitted taxes — plus a free-text note. It is the
 * honest counterpart to `asReportedLedgerSchema` for the MANUAL (non-integrated)
 * path, where there is NO channel payload:
 *
 *   - `asReported` is channel-transcription-ONLY: every line carries
 *     `source: 'channel'` + `provenance`/`remittedBy` enums + a
 *     `Σ(lines) == authoritativeTotalCents` structural check. Re-using it for
 *     hand-entry would stamp a FALSE provenance ("from channel") on data an
 *     operator typed — the exact "fabricate a wrong reality" class SCRUM-350
 *     avoided. So this is a SEPARATE, minimal structure.
 *
 * Provenance discipline (the crux, §user-guide L3): these notes are
 * `operator-supplied`, NOT `channel`. The wire INPUT here deliberately does NOT
 * carry a provenance field — a client can never assert provenance. The server
 * STAMPS `provenance: 'operator-supplied'` when it builds the domain VO, and the
 * READ DTO surfaces that literal so the UI labels the figures honestly
 * ("entered manually"), never "from channel".
 *
 * Strictly informational (§user-guide L3): completely decoupled from the general
 * ledger. These figures NEVER re-price the stay, NEVER touch the folio / taxes /
 * owner accounting; the ChannelCollect $0-balance / net-remittance-verbatim
 * invariant is untouched. The only operation over them is display formatting.
 *
 * Money is `Cents` (CLAUDE.md rule 2): each figure is a non-negative integer-cents
 * wire value, branded `Cents`, so the DTO mapper hands it straight to the domain
 * VO with no re-derivation. No arithmetic is ever performed on these figures.
 */
import { z } from 'zod';
/** Max length of the free-text operator note (a generous single-paragraph bound). */
export const CHANNEL_REFERENCE_NOTE_MAX_LENGTH = 2_000;
/**
 * A single channel-reference money figure at the wire boundary: a non-negative
 * safe integer number of cents, branded `Cents`. Non-negative because gross /
 * commission / channel-tax are all reported as positive magnitudes (a commission
 * is a positive amount the channel retained, not a signed ledger delta — these
 * notes are NOT a ledger). Mirrors `centsWireSchema` in the lifecycle schema.
 */
const referenceCentsSchema = z
    .number()
    .int()
    .nonnegative()
    .max(Number.MAX_SAFE_INTEGER)
    .brand();
/**
 * Operator-supplied Channel Reference Notes (wire INPUT).
 *
 * Every content field is optional — an operator may record any subset (just the
 * gross, just a note, all four). `.strict()` rejects unknown keys, which
 * critically also rejects a client-supplied `provenance` (provenance is
 * server-stamped, never client-assertable). A `.refine` forbids a
 * structurally-empty notes object (no figure AND no note): the FE omits the whole
 * `channelReferenceNotes` field when the operator leaves the section blank, so a
 * present-but-empty object is malformed input, not "no notes".
 */
export const channelReferenceNotesSchema = z
    .object({
    /** The channel's gross guest payment, as the operator read it. */
    grossGuestPaymentCents: referenceCentsSchema.optional(),
    /** The OTA commission the channel retained, as the operator read it. */
    otaCommissionCents: referenceCentsSchema.optional(),
    /** Taxes the channel collected + remitted to the authority, as read. */
    channelRemittedTaxCents: referenceCentsSchema.optional(),
    /** A free-text operator note (trimmed; non-empty when present; length-bounded). */
    note: z.string().trim().min(1).max(CHANNEL_REFERENCE_NOTE_MAX_LENGTH).optional(),
})
    .strict()
    .refine((v) => v.grossGuestPaymentCents !== undefined ||
    v.otaCommissionCents !== undefined ||
    v.channelRemittedTaxCents !== undefined ||
    v.note !== undefined, { message: 'Channel reference notes must carry at least one figure or a note.' });
//# sourceMappingURL=channel-reference-notes.schema.js.map